import * as base64js from 'base64-js'
import { defineModule, localActionContext, localGetterContext } from 'direct-vuex'
import { PracticeRecordingType, Query } from 'types/types'

import DeletePracticeRecording from '@/mutations/DeletePracticeRecording.graphql'
import DeletePracticeRecordings from '@/mutations/DeletePracticeRecordings.graphql'
import MarkPracticeRecordingListened from '@/mutations/MarkPracticeRecordingListened.graphql'
import UpdatePracticeRecording from '@/mutations/UpdatePracticeRecording.graphql'
import MyPracticeRecordingsQuery from '@/queries/MyPracticeRecordingsQuery.graphql'
import PracticeRecordingContents from '@/queries/PracticeRecordingContents.graphql'
import SharedPracticeRecordingsQuery from '@/queries/SharedPracticeRecordingsQuery.graphql'
import { runReportedMutation, runStoreMutation } from '@/store/mutationHelpers'
import { useApollo } from '@/util/apolloClient'
import { createBlobUrlFromBytes } from '@/util/AudioUtil'
import { cloneRaw } from '@/util/util'

const { apolloClient } = useApollo()

export interface PracticeRecordingsState {
  recordings: Array<PracticeRecordingType>
  sharedRecordings: Array<PracticeRecordingType>
  loading: boolean
  audioCache: Record<string, string> // recording id → blob URL
}

const resetState = (): PracticeRecordingsState => {
  return {
    audioCache: {},
    loading: false,
    recordings: [],
    sharedRecordings: [],
  }
}

const practiceRecordings = defineModule({
  actions: {
    clearSharedRecordings(context) {
      const { commit } = practiceRecordingsActionContext(context)
      commit.setSharedRecordings([])
    },

    /** Delete a recording. Resolves to the server's answer, or null (after telling the user why) if it failed. */
    async deleteRecording(context, id: string) {
      const { commit } = practiceRecordingsActionContext(context)
      const payload = await runStoreMutation(context, DeletePracticeRecording, { id }, 'deletePracticeRecording')
      if (payload) {
        commit.removeRecording(id)
        commit.releaseAudio(id)
      }
      return payload
    },

    /** Delete several recordings. Resolves to the server's answer, or null (after telling the user why) if it failed. */
    async deleteRecordings(context, ids: string[]) {
      const { commit } = practiceRecordingsActionContext(context)
      const payload = await runStoreMutation(context, DeletePracticeRecordings, { ids }, 'deletePracticeRecordings')
      if (payload) {
        for (const id of ids) {
          commit.removeRecording(id)
          commit.releaseAudio(id)
        }
      }
      return payload
    },

    async fetchMyRecordings(context) {
      const { commit } = practiceRecordingsActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'myPracticeRecordings'>>({
          fetchPolicy: 'network-only',
          query: MyPracticeRecordingsQuery,
        })
        commit.setRecordings((response.data?.myPracticeRecordings?.filter(Boolean) as PracticeRecordingType[]) ?? [])
      } catch (error) {
        console.error('fetchMyRecordings failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchRecordingAudio(context, id: string): Promise<string | null> {
      const { commit, state } = practiceRecordingsActionContext(context)
      if (state.audioCache[id]) {
        return state.audioCache[id]
      }
      const response = await apolloClient.query<Pick<Query, 'practiceRecordingContents'>>({
        fetchPolicy: 'network-only',
        query: PracticeRecordingContents,
        variables: { id },
      })
      const data = response.data?.practiceRecordingContents
      if (!data?.ok || !data.blob) {
        console.error('fetchRecordingAudio failed:', data?.errors ?? 'no response')
        return null
      }
      const bytes = base64js.toByteArray(data.blob)
      const blobUrl = createBlobUrlFromBytes(bytes)
      commit.cacheAudio({ blobUrl, id })
      return blobUrl
    },

    async fetchSharedRecordings(context, studentId: string) {
      const { commit } = practiceRecordingsActionContext(context)
      commit.setLoading(true)
      console.log('fetchSharedRecordings: studentId =', studentId, 'type =', typeof studentId)
      try {
        const response = await apolloClient.query<Pick<Query, 'sharedPracticeRecordings'>>({
          fetchPolicy: 'network-only',
          query: SharedPracticeRecordingsQuery,
          variables: { studentId },
        })
        console.log('fetchSharedRecordings: got', response.data?.sharedPracticeRecordings?.length, 'recordings')
        commit.setSharedRecordings((response.data?.sharedPracticeRecordings?.filter(Boolean) as PracticeRecordingType[]) ?? [])
      } catch (e) {
        console.error('fetchSharedRecordings failed:', e)
      } finally {
        commit.setLoading(false)
      }
    },

    /** Background bookkeeping: a failure is logged, never shown, and never rejects. */
    async markListened(context, id: string) {
      const { commit } = practiceRecordingsActionContext(context)
      const payload = await runReportedMutation(MarkPracticeRecordingListened, { id }, 'markPracticeRecordingListened', (message) =>
        console.warn('markListened failed:', message),
      )
      if (payload) {
        commit.markSharedRecordingListened(id)
      }
    },

    releaseAudio(context, id: string) {
      const { commit } = practiceRecordingsActionContext(context)
      commit.releaseAudio(id)
    },

    reset(context) {
      const { commit, state } = practiceRecordingsActionContext(context)
      Object.keys(state.audioCache).forEach((id) => {
        commit.releaseAudio(id)
      })
      commit.reset()
    },

    /** Update a recording's notes or sharing. Resolves to the server's answer, or null (after telling the user why) if it failed. */
    async updateRecording(context, changes: { id: string; notes?: string; shared?: boolean }) {
      const { commit } = practiceRecordingsActionContext(context)
      const payload = await runStoreMutation(
        context,
        UpdatePracticeRecording,
        { id: changes.id, notes: changes.notes, shared: changes.shared },
        'updatePracticeRecording',
      )
      if (payload) {
        commit.patchRecording(changes)
      }
      return payload
    },
  },

  getters: {
    getCachedAudioUrl(...args): (id: string) => string | null {
      const { state } = practiceRecordingsGetterContext(args)
      return (id: string) => state.audioCache[id] ?? null
    },

    loading(...args): boolean {
      const { state } = practiceRecordingsGetterContext(args)
      return state.loading
    },

    recordings(...args): Array<PracticeRecordingType> {
      const { state } = practiceRecordingsGetterContext(args)
      return state.recordings
    },

    sharedRecordings(...args): Array<PracticeRecordingType> {
      const { state } = practiceRecordingsGetterContext(args)
      return state.sharedRecordings
    },
  },

  mutations: {
    cacheAudio(state: PracticeRecordingsState, payload: { blobUrl: string; id: string }) {
      state.audioCache[payload.id] = payload.blobUrl
    },

    markSharedRecordingListened(state: PracticeRecordingsState, id: string) {
      const recording = state.sharedRecordings.find((r) => r.id === id)
      if (recording) {
        recording.listenedAt = new Date().toISOString()
      }
    },

    patchRecording(state: PracticeRecordingsState, payload: { id: string; notes?: string; shared?: boolean }) {
      const recording = state.recordings.find((r) => r.id === payload.id)
      if (recording) {
        if (payload.shared !== undefined) {
          recording.shared = payload.shared
        }
        if (payload.notes !== undefined) {
          recording.notes = payload.notes
        }
      }
    },

    releaseAudio(state: PracticeRecordingsState, id: string) {
      const blobUrl = state.audioCache[id]
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl)
        delete state.audioCache[id]
      }
    },

    removeRecording(state: PracticeRecordingsState, id: string) {
      state.recordings = state.recordings.filter((r) => r.id !== id)
    },

    reset(state: PracticeRecordingsState) {
      Object.assign(state, resetState())
    },

    setLoading(state: PracticeRecordingsState, loading: boolean) {
      state.loading = loading
    },

    setRecordings(state: PracticeRecordingsState, recordings: Array<PracticeRecordingType>) {
      state.recordings = cloneRaw(recordings)
    },

    setSharedRecordings(state: PracticeRecordingsState, recordings: Array<PracticeRecordingType>) {
      state.sharedRecordings = cloneRaw(recordings)
    },
  },

  namespaced: true as const,

  state: resetState(),
})

export default practiceRecordings
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const practiceRecordingsGetterContext = (args: [any, any, any, any]) => localGetterContext(args, practiceRecordings)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const practiceRecordingsActionContext = (context: any) => localActionContext(context, practiceRecordings)
